import React, { useEffect, useState, useMemo, useCallback } from "react";
import {
  Box,
  useMediaQuery,
  Accordion,
  AccordionSummary,
  AccordionDetails,
} from "@mui/material";
import {
  ExpandMore as ExpandMoreIcon,
  WorkOutlineOutlined as WorkIcon,
} from "@mui/icons-material";
import { useNavigate } from "react-router-dom";
import useTranslation from "../../../shared/hooks/useTranslation";
import { Offer, OfferStatusCode, OffersState } from "../models/models";
import { useAppDispatch, useAppSelector } from "../../../shared/hooks/sharedHooks";
import {
  getOffers,
  changeOffersQuery,
  confirmOffer,
  startWork,
  clearError,
} from "../customerOffersSlice";
import { useConfirmation } from "../../../shared/components/confirmation/ConfirmationProvider";
import Loader from "../../../shared/components/Loader";
import ActionToast from "../../../shared/components/ActionToast";
import { NotificationMessage } from "../../../shared/models/models";
import { dt } from "../../../shared/themes/redesignTokens";
import { useRedesignSetup } from "../../../shared/hooks/useRedesignSetup";
import useDataRefresh from "../../../shared/notifications/hooks/useDataRefresh";
import useVisibilityRefresh from "../../../shared/hooks/useVisibilityRefresh";
import ActiveWorkColumn, { KANBAN_COLUMNS } from "./ActiveWorkColumn";
import ActiveWorkCard, { columnThemes } from "./ActiveWorkCard";
import ConfirmServiceDialog from "./ConfirmServiceDialog";

// ════════════════════════════════════════════════════════════════
//  Active execution status codes
// ════════════════════════════════════════════════════════════════

const ACTIVE_WORK_STATUSES = [
  OfferStatusCode.awaitingConfirmation,
  OfferStatusCode.confirmed,
  OfferStatusCode.workInProgress,
  OfferStatusCode.workCompleted,
];

// ════════════════════════════════════════════════════════════════
//  Main Component
// ════════════════════════════════════════════════════════════════

const ActiveWork: React.FC = () => {
  const navigate = useNavigate();
  const { translate } = useTranslation(["serviceOffers", "common"]);
  const dispatch = useAppDispatch();
  const confirm = useConfirmation();

  const { offers, loading, error } = useAppSelector<OffersState>(
    (state: any) => state.customerOffers
  );

  // 3-tier responsive: Desktop > 1200, Tablet 768-1200, Phone < 768
  const isTablet = useMediaQuery("(max-width:1200px)");
  const isPhone = useMediaQuery("(max-width:768px)");

  // Confirm dialog state
  const [confirmDialogOpen, setConfirmDialogOpen] = useState(false);
  const [confirmingOffer, setConfirmingOffer] = useState<Offer | null>(null);

  useRedesignSetup();

  // ── Inject extra keyframes for this page ──
  useEffect(() => {
    const styleId = "active-work-keyframes";
    if (!document.getElementById(styleId)) {
      const style = document.createElement("style");
      style.id = styleId;
      style.textContent = `
        @keyframes spin {
          0% { transform: rotate(0deg); }
          100% { transform: rotate(360deg); }
        }
        @keyframes pulse {
          0%, 100% { opacity: 1; }
          50% { opacity: 0.5; }
        }
        @keyframes shimmer {
          0% { background-position: -200% 0; }
          100% { background-position: 200% 0; }
        }
      `;
      document.head.appendChild(style);
    }
  }, []);

  // ── Fetch all offers in active execution states ──
  const fetchActiveWork = useCallback(() => {
    const query = {
      statusCodes: ACTIVE_WORK_STATUSES,
      pageSize: 100, // Get all active work items
      pageNo: 1,
      sortDesc: false,
    };
    dispatch(changeOffersQuery(query));
    dispatch(getOffers(query));
  }, [dispatch]);

  useEffect(() => {
    fetchActiveWork();
  }, [fetchActiveWork]);

  // Real-time refresh
  useDataRefresh("myOffers", fetchActiveWork);
  useVisibilityRefresh(fetchActiveWork);

  // ── Group offers by status code ──
  const groupedOffers = useMemo(() => {
    const items = offers?.items || [];
    const groups: Record<number, Offer[]> = {
      [OfferStatusCode.awaitingConfirmation]: [],
      [OfferStatusCode.confirmed]: [],
      [OfferStatusCode.workInProgress]: [],
      [OfferStatusCode.workCompleted]: [],
    };

    items.forEach((offer) => {
      if (groups[offer.statusCode]) {
        groups[offer.statusCode].push(offer);
      }
    });

    return groups;
  }, [offers]);

  // ── Total active items ──
  const totalActive = useMemo(() => {
    return Object.values(groupedOffers).reduce((sum, arr) => sum + arr.length, 0);
  }, [groupedOffers]);

  // ── Action handlers ──

  const handleConfirmService = (offer: Offer) => {
    setConfirmingOffer(offer);
    setConfirmDialogOpen(true);
  };

  const handleConfirmServiceSubmit = async (data: {
    arrivalDate: string;
    arrivalTimeFrom: string;
    arrivalTimeTo: string;
    message?: string;
  }) => {
    if (!confirmingOffer) return;
    setConfirmDialogOpen(false);
    await dispatch(confirmOffer({ offerId: confirmingOffer.id, data }));
    fetchActiveWork();
    setConfirmingOffer(null);
  };

  const handleStartWork = (offer: Offer) => {
    confirm({
      title: translate("execution.startWorkTitle"),
      text: translate("execution.startWorkText", { number: offer.internalNumber }),
      confirmButton: translate("execution.yesStartWork"),
      closeButton: translate("common:common.no"),
      severity: "neutral",
      onConfirm: async () => {
        await dispatch(startWork(offer.id));
        fetchActiveWork();
      },
    });
  };

  const handleMarkComplete = (offer: Offer) => {
    // Navigate to the offer detail page where the MarkComplete form lives
    navigate(`/service-offers/${offer.id}`);
  };

  const handleViewOffer = (offer: Offer) => {
    navigate(`/service-offers/${offer.id}`);
  };

  // ── Loading state ──
  if (loading && !offers) {
    return <Loader />;
  }

  // ── Phone: accordion layout ──
  const renderPhoneLayout = () => (
    <Box sx={{ display: "flex", flexDirection: "column", gap: "12px" }}>
      {KANBAN_COLUMNS.map((config) => {
        const colOffers = groupedOffers[config.statusCode] || [];
        const theme = columnThemes[config.statusCode];
        const count = colOffers.length;

        return (
          <Accordion
            key={config.statusCode}
            defaultExpanded={count > 0}
            sx={{
              backgroundColor: theme.bg,
              borderRadius: "12px !important",
              border: `1px solid ${dt.cardBorder}`,
              boxShadow: "none !important",
              overflow: "hidden",
              "&:before": { display: "none" },
              "&.Mui-expanded": { margin: "0 !important" },
            }}
          >
            <AccordionSummary
              expandIcon={<ExpandMoreIcon sx={{ color: dt.textSecondary }} />}
              sx={{
                minHeight: "48px",
                px: 2,
                "&.Mui-expanded": { minHeight: "48px" },
                "& .MuiAccordionSummary-content": {
                  alignItems: "center",
                  gap: 1,
                  my: "8px",
                  "&.Mui-expanded": { my: "8px" },
                },
              }}
            >
              <Box
                sx={{
                  width: "8px",
                  height: "8px",
                  borderRadius: "50%",
                  backgroundColor: theme.accent,
                  boxShadow: `0 0 0 3px ${theme.glow}`,
                  flexShrink: 0,
                }}
              />
              <Box sx={{
                fontSize: "13px",
                fontWeight: 700,
                fontFamily: dt.font,
                color: dt.textPrimary,
              }}>
                {translate(config.titleKey)}
              </Box>
              <Box
                sx={{
                  display: "inline-flex",
                  alignItems: "center",
                  justifyContent: "center",
                  minWidth: "20px",
                  height: "20px",
                  borderRadius: "10px",
                  backgroundColor: count > 0 ? theme.accent : dt.inputBg,
                  color: count > 0 ? "#FFFFFF" : dt.textMuted,
                  fontSize: "10px",
                  fontWeight: 700,
                  fontFamily: dt.fontMono,
                  padding: "0 5px",
                  ml: "auto",
                  mr: 1,
                }}
              >
                {count}
              </Box>
            </AccordionSummary>
            <AccordionDetails sx={{ pt: 0, px: 1.5, pb: 1.5 }}>
              {count > 0 ? (
                <Box sx={{ display: "flex", flexDirection: "column", gap: "8px" }}>
                  {colOffers.map((offer, index) => (
                    <ActiveWorkCard
                      key={offer.id}
                      offer={offer}
                      index={index}
                      translate={translate}
                      onConfirmService={handleConfirmService}
                      onStartWork={handleStartWork}
                      onMarkComplete={handleMarkComplete}
                      onViewOffer={handleViewOffer}
                    />
                  ))}
                </Box>
              ) : (
                <Box sx={{
                  color: dt.textMuted,
                  fontSize: "12px",
                  fontFamily: dt.font,
                  textAlign: "center",
                  py: 2,
                }}>
                  {translate(config.emptyKey)}
                </Box>
              )}
            </AccordionDetails>
          </Accordion>
        );
      })}
    </Box>
  );

  // ── Tablet: 2x2 grid ──
  const renderTabletLayout = () => (
    <Box
      sx={{
        display: "grid",
        gridTemplateColumns: "1fr 1fr",
        gap: "16px",
      }}
    >
      {KANBAN_COLUMNS.map((config) => (
        <ActiveWorkColumn
          key={config.statusCode}
          config={config}
          offers={groupedOffers[config.statusCode] || []}
          translate={translate}
          onConfirmService={handleConfirmService}
          onStartWork={handleStartWork}
          onMarkComplete={handleMarkComplete}
          onViewOffer={handleViewOffer}
        />
      ))}
    </Box>
  );

  // ── Desktop: 4 columns side by side ──
  const renderDesktopLayout = () => (
    <Box
      sx={{
        display: "flex",
        gap: "16px",
        alignItems: "flex-start",
      }}
    >
      {KANBAN_COLUMNS.map((config) => (
        <ActiveWorkColumn
          key={config.statusCode}
          config={config}
          offers={groupedOffers[config.statusCode] || []}
          translate={translate}
          onConfirmService={handleConfirmService}
          onStartWork={handleStartWork}
          onMarkComplete={handleMarkComplete}
          onViewOffer={handleViewOffer}
        />
      ))}
    </Box>
  );

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        flex: 1,
        backgroundColor: dt.pageBg,
        backgroundImage: `radial-gradient(circle at 1px 1px, ${dt.pageDotColor} 1px, transparent 0)`,
        backgroundSize: "28px 28px",
        minHeight: "100%",
        fontFamily: dt.font,
        overflowX: "hidden",
        px: { xs: "12px", sm: "16px", md: "32px" },
        "& *:not(.MuiSvgIcon-root)": { fontFamily: `${dt.font}` },
      }}
    >
      <ActionToast
        open={!!error}
        variant="error"
        message={translate(NotificationMessage.error(error).message)}
        onClose={() => dispatch(clearError())}
        duration={6000}
      />

      {/* ── Page header ── */}
      <Box
        sx={{
          pt: { xs: 2, md: 3 },
          pb: 2,
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          animation: "headerReveal 0.4s cubic-bezier(0.4, 0, 0.2, 1) both",
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: "12px" }}>
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              width: "36px",
              height: "36px",
              borderRadius: "10px",
              background: "linear-gradient(135deg, #3B82F6 0%, #2563EB 100%)",
              boxShadow: "0 2px 8px rgba(59,130,246,0.25)",
            }}
          >
            <WorkIcon sx={{ fontSize: "20px", color: "#FFFFFF" }} />
          </Box>
          <Box>
            <Box
              sx={{
                fontSize: { xs: "18px", md: "22px" },
                fontWeight: 700,
                fontFamily: dt.font,
                color: dt.textPrimary,
                lineHeight: 1.2,
              }}
            >
              {translate("activeWork.title")}
            </Box>
            <Box
              sx={{
                fontSize: "12px",
                fontFamily: dt.font,
                color: dt.textMuted,
                mt: "2px",
              }}
            >
              {totalActive > 0
                ? translate("activeWork.subtitle", { count: totalActive })
                : translate("activeWork.subtitleEmpty")
              }
            </Box>
          </Box>
        </Box>
      </Box>

      {/* ── Kanban board ── */}
      <Box sx={{ flex: 1, pb: 3 }}>
        {totalActive === 0 && !loading ? (
          /* Empty state */
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              justifyContent: "center",
              minHeight: "400px",
              backgroundColor: dt.cardBg,
              borderRadius: dt.radiusLg,
              border: `1px solid ${dt.cardBorder}`,
              boxShadow: dt.cardShadow,
              padding: "48px",
              animation: "fadeUp 0.5s cubic-bezier(0.4, 0, 0.2, 1) 0.15s both",
            }}
          >
            <Box
              sx={{
                width: "64px",
                height: "64px",
                borderRadius: "16px",
                background: "linear-gradient(135deg, rgba(59,130,246,0.08) 0%, rgba(59,130,246,0.04) 100%)",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                mb: 3,
              }}
            >
              <WorkIcon sx={{ fontSize: "32px", color: dt.blue, opacity: 0.6 }} />
            </Box>
            <Box
              sx={{
                fontSize: "16px",
                fontWeight: 600,
                fontFamily: dt.font,
                color: dt.textPrimary,
                mb: 1,
                textAlign: "center",
              }}
            >
              {translate("activeWork.emptyTitle")}
            </Box>
            <Box
              sx={{
                fontSize: "13px",
                fontFamily: dt.font,
                color: dt.textMuted,
                textAlign: "center",
                maxWidth: "400px",
                lineHeight: "20px",
              }}
            >
              {translate("activeWork.emptyDescription")}
            </Box>
          </Box>
        ) : (
          /* Responsive board */
          <>
            {isPhone
              ? renderPhoneLayout()
              : isTablet
                ? renderTabletLayout()
                : renderDesktopLayout()
            }
          </>
        )}
      </Box>

      {/* ── Confirm Service Dialog ── */}
      <ConfirmServiceDialog
        open={confirmDialogOpen}
        offerNumber={confirmingOffer?.internalNumber || ""}
        defaultArrivalDate={confirmingOffer?.earliestStartDate}
        onConfirm={handleConfirmServiceSubmit}
        onClose={() => {
          setConfirmDialogOpen(false);
          setConfirmingOffer(null);
        }}
      />
    </Box>
  );
};

export default ActiveWork;
